import * as React from 'react';

import {Button} from 'shells/button/button';
import {Dialog} from 'shells/dialog/dialog';
import type {ImportType} from 'shells/dromo/types';
import {Link} from 'shells/link/link';
import {DisplayOnlyTable} from 'shells/table/display-only-table';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {Body} from 'shells/typography/body';

import type {GetDromoImports_dromoImports_imports as DromoImportRow} from 'nutshell-graphql-types';

import {useRevertDromoImport} from '../graphql/hooks/use-revert-dromo-import';

import styles from './dromo-imports-table.module.css';

type Props = {
    importHistory: DromoImportRow[];
    isLoading: boolean;
    errorMessage: string | null | undefined;
    importType: ImportType;
};

export function DromoImportsTable(props: Props) {
    const [revertingImportId, setRevertingImportId] = React.useState<string | null>(null);
    const [confirmingImportId, setConfirmingImportId] = React.useState<string | null>(null);
    const [dialogAnchor, setDialogAnchor] = React.useState<HTMLElement | null>(null);
    const {revertDromoImport} = useRevertDromoImport();

    const handleConfirmRevertImport = React.useCallback(async () => {
        if (!confirmingImportId || revertingImportId) {
            return;
        }

        const importIdToRevert = confirmingImportId;
        setConfirmingImportId(null);
        setDialogAnchor(null);
        setRevertingImportId(importIdToRevert);

        try {
            await revertDromoImport({originId: importIdToRevert});
        } finally {
            setRevertingImportId(null);
        }
    }, [confirmingImportId, revertDromoImport, revertingImportId]);

    const handleOpenRevertImportConfirmation = React.useCallback(
        (importId: string, anchor: HTMLElement) => {
            setConfirmingImportId(importId);
            setDialogAnchor(anchor);
        },
        []
    );

    const columns = React.useMemo(
        () => [
            {
                id: 'status',
                Header: 'Status',
                dataType: 'string',
                // @ts-expect-error
                accessor: (row) => row.displayStatus,
                maxWidth: 100,
            },
            {
                id: 'user',
                Header: 'User',
                dataType: 'string',
                // @ts-expect-error
                accessor: (row) => row.initiatingUser.name,
                shouldTruncate: true,
            },
            {
                id: 'startTime',
                Header: 'Start time',
                dataType: 'string',
                // @ts-expect-error
                accessor: (row) => row.startTime,
                maxWidth: 128,
                Cell: ({row}: any) => {
                    return (
                        row.original.startTime && (
                            <FormattedTimestamp
                                datetime={row.original.startTime}
                                format={FormattedTimestamp.formats.shortMonthDayWithTime}
                            />
                        )
                    );
                },
            },
            {
                id: 'fileName',
                Header: 'File uploaded',
                dataType: 'string',
                shouldTruncate: true,
                // @ts-expect-error
                accessor: (row) => {
                    return (
                        <div title={row.dromoRawFileUrl ? 'Download file' : 'Download unavailable'}>
                            {row.dromoRawFileUrl ? (
                                <Link
                                    href={row.dromoRawFileUrl}
                                    newTab={true}
                                    variant='primary'
                                    size='small'
                                >
                                    {row.fileName}
                                </Link>
                            ) : (
                                row.fileName
                            )}
                        </div>
                    );
                },
            },
            {
                id: 'actions',
                Header: '',
                dataType: 'component',
                // @ts-expect-error
                accessor: (row) => row,
                disableSortBy: true,
                maxWidth: 120,
                Cell: ({cell}: any) => {
                    const importId = cell.value.id;

                    if (!cell.value.canRevert) {
                        return null;
                    }

                    return (
                        <Button
                            variant='text-primary'
                            onClick={(e) => {
                                handleOpenRevertImportConfirmation(
                                    importId,
                                    e.currentTarget as HTMLElement
                                );
                            }}
                            disabled={revertingImportId === importId}
                            noPadding={true}
                        >
                            Revert
                        </Button>
                    );
                },
            },
        ],
        [handleOpenRevertImportConfirmation, revertingImportId]
    );

    return (
        <>
            <div className='border-dk br-3 overflow-hidden'>
                <div className={styles['import-table-header']}>
                    <Body size='large' isBold={true} color='white'>
                        Import history
                    </Body>
                </div>
                <DisplayOnlyTable
                    // @ts-expect-error
                    columns={columns}
                    data={props.importHistory}
                    isLoading={props.isLoading}
                    error={props.errorMessage}
                    disableSorting={false}
                    sortBy={[{id: 'startTime', desc: true}]}
                    emptyStateText='No imports found'
                    emptyStateSubtitle={`Make your first ${props.importType} import!`}
                    isStriped={true}
                />
            </div>
            {confirmingImportId && (
                <Dialog
                    anchor={dialogAnchor}
                    question={`Are you sure you want to revert this import? This will delete all ${props.importType} created during the import.`}
                    primaryText={`Yes, delete ${props.importType}`}
                    tertiaryText='No'
                    onPrimary={handleConfirmRevertImport}
                    onTertiary={() => {
                        setConfirmingImportId(null);
                        setDialogAnchor(null);
                    }}
                    primaryVariant='danger'
                    tertiaryVariant='secondary'
                    onBlur={() => {
                        setConfirmingImportId(null);
                        setDialogAnchor(null);
                    }}
                />
            )}
        </>
    );
}
